/**
 * AD Kerberos queries - Kerberoasting, AS-REP roasting, and weak encryption.
 */

import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

export const AD_KERBEROS_QUERIES: SavedQuery[] = [
  {
    id: 'kerberoastable-users',
    name: 'Kerberoastable Users',
    type: 'table',
    description:
      'Identify user accounts with SPNs set. These are prime candidates for Kerberoasting attacks',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'hasspn', operator: '=', value: true, not: false },
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-502',
            not: true,
          },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
  {
    id: 'asrep-roastable-users',
    name: 'AS-REP Roastable Users',
    type: 'table',
    description:
      'Find users with Kerberos pre-authentication disabled. These can be attacked without authentication.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'dontreqpreauth', operator: '=', value: true, not: false },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
  {
    id: 'principals-weak-kerberos-encryption',
    name: 'Principals with Weak Kerberos Encryption',
    type: 'table',
    description:
      'Find principals supporting weak Kerberos encryption types (DES, RC4) that are vulnerable to offline cracking.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          {
            field: '',
            operator: 'OR',
            value: [
              {
                field: 'supportedencryptiontypes',
                operator: 'CONTAINS',
                value: 'DES',
              },
              {
                field: 'supportedencryptiontypes',
                operator: 'CONTAINS',
                value: 'RC4',
              },
            ],
          },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'principals-des-only-kerberos',
    name: 'Principals with DES-Only Kerberos',
    type: 'table',
    description:
      'Find principals configured to use only DES encryption for Kerberos. DES is deprecated and vulnerable.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          { field: 'usedeskeyonly', operator: '=', value: true, not: false },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'kerberoastable-tier-zero-members',
    name: 'Kerberoastable Members of Tier Zero / High Value Groups',
    type: 'table',
    description:
      'Find Kerberoastable users tagged as tier-zero. These high-value accounts with SPNs are critical targets.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'hasspn', operator: '=', value: true, not: false },
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-502',
            not: true,
          },
          {
            field: 'tags',
            operator: 'IN',
            value: 'tier-zero',
            reverseOperands: true,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
];
